// Pure: how the Learnings page reads the server's figures. Every number is the server's; this only
// formats, tones and arranges them, so it is all testable without a DOM.
import type { LearningChangeType, LearningPageType, LearningTier, OpportunityKind } from 'definitions';
import type { LearningsPageResponse, PageOpportunity, PagePattern } from '@@/api/learnings/page/page-types';
import type { PatternGroup, Surprise } from '@@/api/learnings/patterns/pattern-types';
import { LEARNING_CHANGE_TYPE_LABELS, LEARNING_PAGE_TYPE_LABELS } from 'definitions';

/** A reading's tone: its chip, its band and its dot all take it. */
export type Tone = 'pos' | 'neg' | 'mix' | 'sur' | 'dim' | 'warn';

export const TONE_CHIP: Record<Tone, string> = {
  pos: 'border-ok-line text-ok',
  neg: 'border-danger-line text-danger',
  mix: 'border-bar-muted text-dim',
  sur: 'border-accent-line text-accent',
  dim: 'border-line text-dim',
  warn: 'border-warn-line text-warn',
};

/** The range's pill. */
export const TONE_BAND: Record<Tone, string> = {
  pos: 'bg-ok-line',
  neg: 'bg-danger-line',
  mix: 'bg-bar-muted',
  sur: 'bg-accent-line',
  dim: 'bg-track',
  warn: 'bg-warn-line',
};

/** The point estimate's dot, and the lift's colour beside it. */
export const TONE_DOT: Record<Tone, string> = {
  pos: 'bg-ok',
  neg: 'bg-danger',
  mix: 'bg-dim',
  sur: 'bg-accent',
  dim: 'bg-dim',
  warn: 'bg-warn',
};

export const TONE_TEXT: Record<Tone, string> = {
  pos: 'text-ok',
  neg: 'text-danger',
  mix: 'text-dim',
  sur: 'text-accent',
  dim: 'text-dim',
  warn: 'text-warn',
};

/** "+2.8%", "−4.0%": signed to one decimal with a true minus sign, from a fraction. */
export const signedPercent = (fraction: number): string => {
  const value = Math.round(fraction * 1000) / 10;
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(1)}%`;
};

/** "−5.4 to +10.9": a range without the percent signs, for meta lines. */
export const signedRange = (lower: number, upper: number): string =>
  `${signedPercent(lower).replace('%', '')} to ${signedPercent(upper).replace('%', '')}`;

type Pooled = { mean: number; lower: number; upper: number } | null;

/**
 * What a pattern reads as, in the design's words. The tier is the server's; the direction is the
 * estimate's sign, so a range that still crosses zero reads "Leaning", never "Works".
 */
export function patternTag({ tier, pooled }: { tier: LearningTier; pooled: Pooled }): { tag: string; tone: Tone } {
  if (tier === 'mixed') return { tag: 'Mixed', tone: 'mix' };
  if (!pooled) return { tag: 'No reading', tone: 'dim' };
  if (tier === 'small-effect') return { tag: 'Little difference', tone: 'dim' };
  const up = pooled.mean >= 0;
  if (tier === 'consistent')
    return { tag: up ? 'Conclusive positive' : 'Conclusive negative', tone: up ? 'pos' : 'neg' };
  return { tag: up ? 'Leaning positive' : 'Leaning negative', tone: up ? 'pos' : 'neg' };
}

export const SURPRISE_TAG = { tag: 'Surprise', tone: 'sur' as Tone };

/** What made a test a surprise, as a title: a win nobody predicted, a loss on a high score, or the opposite of the team's read. */
export const surpriseTitle = ({ name, reasons }: Pick<Surprise, 'name' | 'reasons'>): string => {
  if (reasons.includes('win-unexpected')) return `${name} won when nothing predicted it`;
  if (reasons.includes('loss-on-high-pxl')) return `${name} lost despite a high score`;
  return `${name} went the other way from what the team expected`;
};

/** A bar's positions on a scale, as percentages of its width. */
export type BarGeometry = { zero: number; mid: number; lo: number; width: number };

export type Scale = { min: number; max: number };

/**
 * A scale in whole steps of five points that holds every value given, and never less than −15 to
 * +15: a narrow scale would make a two-point lean look like a landslide.
 */
export function scaleFor(values: readonly number[]): Scale {
  const step = 0.05;
  const low = Math.min(-0.15, ...values);
  const high = Math.max(0.15, ...values);
  // The nudge is inwards: a value already on a step must not be rounded out to the next one.
  return { min: Math.floor(low / step + 1e-9) * step, max: Math.ceil(high / step - 1e-9) * step };
}

const at = (value: number, { min, max }: Scale): number =>
  Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

type BarParams = { mean: number; lower?: number | null; upper?: number | null; scale: Scale };

export function barGeometry({ mean, lower, upper, scale }: BarParams): BarGeometry {
  const lo = at(lower ?? mean, scale);
  const hi = at(upper ?? mean, scale);
  return { zero: at(0, scale), mid: at(mean, scale), lo, width: Math.max(0, hi - lo) };
}

/** Axis ticks every fifteen points from the scale's ends, always including zero. */
export function scaleTicks({ min, max }: Scale): number[] {
  const ticks = new Set<number>([0, min, max]);
  for (let value = 0.15; value < max; value += 0.15) ticks.add(Math.round(value * 100) / 100);
  for (let value = -0.15; value > min; value -= 0.15) ticks.add(Math.round(value * 100) / 100);
  return [...ticks].sort((a, b) => a - b);
}

/** "+15%", "0", "−15%": an axis label, in whole points. */
const tickLabel = (value: number): string => {
  const points = Math.round(value * 100);
  return points === 0 ? '0' : `${points > 0 ? '+' : '−'}${Math.abs(points)}%`;
};

/** The ticks of a scale with their positions, for the axis over a column of bars. */
export const axisTicks = (scale: Scale) =>
  scaleTicks(scale).map((value) => ({ value, at: at(value, scale), label: tickLabel(value) }));

export const pageLabel = (pageType: LearningPageType | null): string =>
  pageType ? (LEARNING_PAGE_TYPE_LABELS[pageType] ?? pageType) : 'Several pages';

export const changeTypeLabel = (changeType: LearningChangeType): string =>
  LEARNING_CHANGE_TYPE_LABELS[changeType] ?? changeType;

// --- the evidence map --------------------------------------------------------------------

export type Grouping = 'cell' | 'theme' | 'element' | 'mechanism';

const GROUPING_KIND: Record<Grouping, string> = {
  cell: 'Page',
  theme: 'Theme',
  element: 'Element',
  mechanism: 'Mechanism',
};

/** The most specific grouping of one set of tests is the row; the rest are the same evidence. */
const SPECIFICITY: Record<Grouping, number> = { theme: 0, element: 1, cell: 2, mechanism: 3 };

export type MapRow = {
  key: string;
  label: string;
  /** "Page", "Element · same tests ×3" */
  kind: string;
  grouping: Grouping | 'surprise';
  tone: Tone;
  mean: number;
  lower: number | null;
  upper: number | null;
  tests: number;
};

const testSet = (group: Pick<PatternGroup, 'members'>): string =>
  [...new Set(group.members.map((member) => member.experimentId))].sort((a, b) => a - b).join(',');

type KeyedGroup = PatternGroup & { grouping: Grouping };

/** Every grouping in the evidence, keyed and labelled, with a reading. */
export const evidenceGroups = (evidence: LearningsPageResponse['evidence']): KeyedGroup[] => [
  ...evidence.themes.map((group) => ({ ...group, grouping: 'theme' as const })),
  ...evidence.elements.map((group) => ({ ...group, grouping: 'element' as const })),
  ...evidence.cells.map((group) => ({ ...group, grouping: 'cell' as const })),
  ...evidence.mechanisms.map((group) => ({ ...group, grouping: 'mechanism' as const })),
];

type MapRowsParams = {
  evidence: LearningsPageResponse['evidence'];
  labels: LearningsPageResponse['labels'];
  surprises: readonly Surprise[];
  /**
   * Merge patterns that hold exactly the same tests into one row (the evidence map). Off for a view
   * of one grouping, where each pattern is a row of its own.
   */
  merge?: boolean;
};

/**
 * Every pattern on one axis, with patterns that hold exactly the same tests merged into one row
 * named after the most specific of them ("same tests ×3"), surprises first, then strongest first.
 */
export function mapRows({ evidence, labels, surprises, merge = true }: MapRowsParams): MapRow[] {
  const sets = new Map<string, KeyedGroup[]>();
  for (const group of evidenceGroups(evidence)) {
    if (!group.pooled) continue;
    const key = merge ? testSet(group) : group.key;
    sets.set(key, [...(sets.get(key) ?? []), group]);
  }

  const rows = [...sets.values()].map((groups): MapRow => {
    const [lead] = [...groups].sort((a, b) => SPECIFICITY[a.grouping] - SPECIFICITY[b.grouping]);
    const group = lead!;
    const pooled = group.pooled!;
    const same = groups.length > 1 ? ` · same tests ×${groups.length}` : '';
    return {
      key: group.key,
      label: labels[group.key] ?? group.key,
      kind: `${GROUPING_KIND[group.grouping]}${same}`,
      grouping: group.grouping,
      tone: patternTag(group).tone,
      mean: pooled.mean,
      lower: pooled.lower,
      upper: pooled.upper,
      tests: group.tests,
    };
  });

  const surpriseRows = surprises
    .filter((surprise) => surprise.lift !== null)
    .map(
      (surprise): MapRow => ({
        key: `surprise:${surprise.experimentId}`,
        label: surprise.name,
        kind: 'Surprise',
        grouping: 'surprise',
        tone: 'sur',
        mean: (surprise.lift ?? 0) / 100,
        lower: null,
        upper: null,
        tests: 1,
      }),
    );

  return [...surpriseRows, ...rows.sort((a, b) => b.mean - a.mean)];
}

export type MapFilter = 'all' | 'pages' | 'elements' | 'mechanisms';

/** Themes sit with elements: both are about one thing on the page. Surprises show under All only. */
export const filterMapRows = (rows: readonly MapRow[], filter: MapFilter): MapRow[] =>
  rows.filter((row) => {
    if (filter === 'all') return true;
    if (filter === 'pages') return row.grouping === 'cell';
    if (filter === 'elements') return row.grouping === 'element' || row.grouping === 'theme';
    return row.grouping === 'mechanism';
  });

// --- the page × change type grid ---------------------------------------------------------

export type GridCell =
  | { kind: 'untested' }
  | { kind: 'no-reading'; tests: number; key: string }
  | { kind: 'reading'; tests: number; mean: number; key: string };

export type Grid = {
  pages: LearningPageType[];
  rows: { changeType: LearningChangeType; cells: GridCell[] }[];
};

/** Pages by how many tests ran there, change types by how often they were tried; "other" last on both. */
export function buildGrid(cells: LearningsPageResponse['evidence']['cells']): Grid {
  const testsOn = new Map<LearningPageType, Set<number>>();
  const tries = new Map<LearningChangeType, number>();
  for (const cell of cells) {
    testsOn.set(
      cell.pageType,
      new Set([...(testsOn.get(cell.pageType) ?? []), ...cell.members.map((m) => m.experimentId)]),
    );
    tries.set(cell.changeType, (tries.get(cell.changeType) ?? 0) + cell.tests);
  }
  const lastIfOther = <Key extends string>(a: Key, b: Key) => Number(a === 'other') - Number(b === 'other');
  const pages = [...testsOn.keys()].sort(
    (a, b) => lastIfOther(a, b) || (testsOn.get(b)?.size ?? 0) - (testsOn.get(a)?.size ?? 0),
  );
  const changeTypes = [...tries.keys()].sort((a, b) => lastIfOther(a, b) || (tries.get(b) ?? 0) - (tries.get(a) ?? 0));

  return {
    pages,
    rows: changeTypes.map((changeType) => ({
      changeType,
      cells: pages.map((pageType): GridCell => {
        const cell = cells.find((entry) => entry.changeType === changeType && entry.pageType === pageType);
        if (!cell) return { kind: 'untested' };
        if (!cell.pooled) return { kind: 'no-reading', tests: cell.tests, key: cell.key };
        return { kind: 'reading', tests: cell.tests, mean: cell.pooled.mean, key: cell.key };
      }),
    })),
  };
}

/** A cell's wash: stronger with a bigger lift, never past a quarter so the figure stays readable. */
export const gridStrength = (mean: number): number => Math.min(26, 6 + Math.abs(mean * 100) * 4);

// --- key learnings -----------------------------------------------------------------------

export type KeyLearning =
  | { kind: 'surprise'; surprise: Surprise }
  | { kind: 'pattern'; pattern: PagePattern; tone: Tone; tag: string };

/**
 * The three things most worth acting on: the newest surprise (it asks to be explained), the
 * strongest positive lean (worth confirming), and the clearest negative one (worth stopping). A
 * pattern the team marked known is skipped: they have it. Fewer than three when there are not.
 */
export function keyLearnings({
  featured,
  surprises,
}: Pick<LearningsPageResponse, 'featured' | 'surprises'>): KeyLearning[] {
  const fresh = featured.filter((pattern) => pattern.feedback !== 'known' && pattern.pooled);
  const surprise = [...surprises].sort((a, b) => (b.concludedAt ?? 0) - (a.concludedAt ?? 0))[0];
  const best = fresh
    .filter((pattern) => (pattern.pooled?.mean ?? 0) > 0 && pattern.tier !== 'mixed' && pattern.tier !== 'small-effect')
    .sort((a, b) => (b.pooled?.lower ?? -1) - (a.pooled?.lower ?? -1))[0];
  const worst = fresh
    .filter((pattern) => (pattern.pooled?.mean ?? 0) < 0 && pattern.tier !== 'mixed' && pattern.tier !== 'small-effect')
    .sort((a, b) => (a.pooled?.upper ?? 1) - (b.pooled?.upper ?? 1))[0];

  const asKey = (pattern: PagePattern): KeyLearning => ({ kind: 'pattern', pattern, ...patternTag(pattern) });
  const picked: KeyLearning[] = [
    ...(surprise ? [{ kind: 'surprise' as const, surprise }] : []),
    ...(best ? [asKey(best)] : []),
    ...(worst ? [asKey(worst)] : []),
  ];
  // Short of three: the next strongest featured patterns, so the row is never half empty for no reason.
  const taken = new Set(picked.flatMap((entry) => (entry.kind === 'pattern' ? [entry.pattern.key] : [])));
  const rest = fresh.filter((pattern) => !taken.has(pattern.key)).map(asKey);
  return [...picked, ...rest].slice(0, 3);
}

// --- one pattern -------------------------------------------------------------------------

export type PatternDetail = Pick<
  PagePattern,
  | 'key'
  | 'label'
  | 'grouping'
  | 'tier'
  | 'pooled'
  | 'tests'
  | 'members'
  | 'pageType'
  | 'sameTestsAs'
  | 'wording'
  | 'feedback'
> & {
  /** Whether the page features it (and so whether the wording job words it). */
  featured: boolean;
  /** An open question raised from it, if any. */
  opportunity: PageOpportunity | null;
};

/**
 * A pattern by key, featured or not. A pattern opened from the evidence map may not be featured:
 * it has its figures and members but no words, and its same-tests list is read off the evidence.
 */
export function patternDetail(page: LearningsPageResponse, key: string): PatternDetail | null {
  const opportunity = page.opportunities.find((entry) => entry.pattern?.key === key) ?? null;
  const featured = page.featured.find((pattern) => pattern.key === key);
  if (featured) return { ...featured, featured: true, opportunity };

  const groups = evidenceGroups(page.evidence);
  const group = groups.find((entry) => entry.key === key);
  if (!group) return null;
  const tests = testSet(group);
  const pages = new Set(
    page.evidence.cells
      .filter((cell) => cell.members.some((member) => tests.split(',').includes(String(member.experimentId))))
      .map((cell) => cell.pageType),
  );
  return {
    key,
    label: page.labels[key] ?? key,
    grouping: group.grouping,
    tier: group.tier,
    pooled: group.pooled,
    tests: group.tests,
    members: group.members,
    pageType: pages.size === 1 ? ([...pages][0] ?? null) : null,
    sameTestsAs: groups
      .filter((entry) => entry.key !== key && testSet(entry) === tests)
      .map((entry) => ({ key: entry.key, label: page.labels[entry.key] ?? entry.key })),
    wording: null,
    feedback: null,
    featured: false,
    opportunity,
  };
}

/** A pattern's arms grouped into its tests, newest first: one row per test under "Contributing tests". */
export const testsOfPattern = (members: PagePattern['members']) => {
  const byTest = new Map<number, PagePattern['members'][number][]>();
  for (const member of members) byTest.set(member.experimentId, [...(byTest.get(member.experimentId) ?? []), member]);
  return [...byTest.entries()]
    .map(([experimentId, arms]) => ({
      experimentId,
      name: arms[0]?.name ?? '',
      outcome: arms[0]?.outcome ?? 'no-data',
      concludedAt: arms[0]?.concludedAt ?? null,
      arms,
    }))
    .sort((a, b) => (b.concludedAt ?? 0) - (a.concludedAt ?? 0));
};

// --- opportunities -----------------------------------------------------------------------

export { OPPORTUNITY_KIND_LABELS } from 'definitions';

export const OPPORTUNITY_KIND_TONE: Record<OpportunityKind, Tone> = {
  'explain-surprise': 'sur',
  replicate: 'pos',
  'resolve-mixed': 'mix',
  isolate: 'neg',
  'fill-gap': 'dim',
};

/** The filter pills, in the design's order, each matching one kind. */
export const OPPORTUNITY_FILTERS: { label: string; value: OpportunityKind | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Surprises', value: 'explain-surprise' },
  { label: 'Replicate', value: 'replicate' },
  { label: 'Mixed', value: 'resolve-mixed' },
  { label: 'Isolate', value: 'isolate' },
  { label: 'Gaps', value: 'fill-gap' },
];

/** "Listing page · Desktop": where the question is about. */
export const opportunityWhere = (opportunity: Pick<PageOpportunity, 'pageType' | 'devices' | 'splitBy'>): string =>
  [pageLabel(opportunity.pageType), opportunity.splitBy === 'device' ? 'by device' : opportunity.devices.join(', ')]
    .filter(Boolean)
    .join(' · ');
